import { runAnalytics } from "../../utils/runAnalytics";
import { withConsentPilot } from "../../utils/consent";
import { getPostPurchaseRedirect } from "../../utils/postPurchaseRedirect";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import axios from "axios";
import { getPaymentLimitMessage } from "../../utils/paymentLimitMessage";
import { loadStripe } from "../../utils/checkoutStripe";
import { API_URL, REDIRECT_LINK, STRIPE_PUBLIC_KEY } from "../../constants/formConstants";
import { getAcquisitionCountryMetadataFromSearch } from "../../utils/acquisitionCountry";
import { getCheckoutMetadataFromSearch as getUtmMetadataFromSearch } from "../../utils/checkoutMetadata";
import { UPSELL_OFFERS } from "../../constants/upsells";
import icon111Image from "../../assets/images/icon111.jpeg";
import icon22Image from "../../assets/images/icon22.jpeg";
import icon3Image from "../../assets/images/icon3.jpeg";
import { pushPurchaseEvent } from "../../utils/gtm";
import { getClientReferenceId, sendAmplitudePurchase } from "../../utils/amplitude";
import { sendPaywallEvent } from "../../utils/paywallAnalytics";

const offers = [
  {
    id: "combo",
    title: "Complete Look Bundle (Hair + Outfits + Try-On)",
    oldPrice: "$40.00",
    newPrice: "$19.99 only!",
    badge: "50% OFF",
    description: "",
    iconBg: "#FFE6D1",
    iconColor: "#7C3A8D",
    image: icon111Image,
  },
  {
    id: "hair",
    title: "Hair Color and Hairstyles Analysis with Try-On",
    oldPrice: "$22.00",
    newPrice: "$14.99",
    badge: "32% OFF",
    description: "Get your best Hair colors & Hairstyles",
    iconBg: "#E0E7FF",
    iconColor: "#5B21B6",
    image: icon22Image,
  },
  {
    id: "outfits",
    title: "Outfits for Your Color Type with Try-On",
    oldPrice: "$22.00",
    newPrice: "$14.99",
    badge: "32% OFF",
    description: "Get Outfits for your color type with Try On",
    iconBg: "#E9F5F2",
    iconColor: "#047857",
    image: icon3Image,
  },
];

const benefits = [
  "One-time payment, no surprise renewals.",
  "Smart guidance: outfits and hair by your season.",
  "Virtual try-on for hair and outfits before you commit.",
];

const IndicatorIcon = ({ selected }) => {
  if (selected) {
    return (
      <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-[#7C3A8D] text-white flex items-center justify-center shadow-sm">
        ✓
      </div>
    );
  }
  return (
    <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full border border-gray-300 flex items-center justify-center bg-white">
      <div className="h-3 w-3 sm:h-4 sm:w-4 rounded-full bg-gray-200" />
    </div>
  );
};

const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

const GetOffer = () => {
  const [selected, setSelected] = useState(offers[0].id);
  const [loading, setLoading] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const active = useRef(true);
  const leaving = useRef(false);
  const submitting = useRef(false);
  // History state belongs to this entry, not the next checkout in this tab.
  const markFinished = () => {
    try { window.history.replaceState({ ...window.history.state, legacyUpsellFinished: true }, ""); } catch (_) {}
  };
  useEffect(() => {
    active.current = true;
    if (window.history.state?.legacyUpsellFinished && !leaving.current) {
      leaving.current = true;
      window.location.replace(withConsentPilot(getPostPurchaseRedirect(REDIRECT_LINK, window.location.search)));
    }
    // BFCache restores refs without remounting. A completed offer stays completed.
    // https://developer.mozilla.org/en-US/docs/Web/API/Window/pageshow_event
    const resume = (event) => {
      if (event.persisted && leaving.current)
        window.location.replace(withConsentPilot(getPostPurchaseRedirect(REDIRECT_LINK, window.location.search)));
    };
    // Back can freeze the document without React unmounting it.
    const leave = () => {
      if (submitting.current) leaving.current = true;
    };
    window.addEventListener("pagehide", leave);
    window.addEventListener("pageshow", resume);
    return () => {
      active.current = false;
      window.removeEventListener("pagehide", leave);
      window.removeEventListener("pageshow", resume);
    };
  }, []);
  const canContinue = () => active.current && !leaving.current;

  // Ensure page always opens at the top (SPA navigation and post-payment redirects)
  useLayoutEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    } catch (_) {}
  }, []);

  useEffect(() => {
    sendPaywallEvent("upsell_seen", { route: "/getoffer" });
  }, []);

  const goRedirect = () => {
    if (!canContinue()) return;
    leaving.current = true;
    markFinished();
    window.location.href = withConsentPilot(getPostPurchaseRedirect(REDIRECT_LINK, window.location.search));
  };

  const handleSkip = () => {
    if (!canContinue()) return;
    try {
      Promise.resolve(sendPaywallEvent("upsell_skipped", {
        route: "/getoffer",
        payload: { offer_id: selected },
      })).catch(() => {});
    } catch (_) {}
    goRedirect();
  };

  const handleOfferSelect = (offerId) => {
    setSelected(offerId);
    sendPaywallEvent("upsell_offer_selected", {
      route: "/getoffer",
      payload: { offer_id: offerId },
    });
  };

  const handleGetOffer = async () => {
    if (!canContinue() || submitting.current) return;
    submitting.current = true;
    markFinished();
    sendPaywallEvent("upsell_cta_clicked", {
      route: "/getoffer",
      payload: { offer_id: selected },
    });
    setLoading(true);
    setPaymentError("");
    try {
      const offer = UPSELL_OFFERS[selected];
      if (!offer) {
        goRedirect();
        return;
      }

      let customerId = null;
      let paymentMethodId = null;
      try {
        customerId = sessionStorage.getItem("stripeCustomerId");
        paymentMethodId = sessionStorage.getItem("stripePaymentMethodId");
      } catch (_) {}

      if (!customerId || !paymentMethodId) {
        goRedirect();
        return;
      }

      const stripe = await stripePromise;
      if (!canContinue()) return;
      if (!stripe) {
        goRedirect();
        return;
      }

      const searchParams = new URLSearchParams(window.location.search);
      const clientReferenceId = searchParams.get("client_reference_id");
      const resolvedClientReferenceId = getClientReferenceId();
      const utmMetadata = getUtmMetadataFromSearch(window.location.search, { maxKeys: 46 });
      const acquisitionCountryMetadata = getAcquisitionCountryMetadataFromSearch(
        window.location.search,
      );
      const lastPaywall = (() => {
        try {
          return sessionStorage.getItem("lastPaywall") || "getoffer";
        } catch (_) {
          return "getoffer";
        }
      })();
      const offerValue = (() => {
        const found = offers.find((o) => o.id === selected);
        if (!found?.newPrice) return undefined;
        const parsed = Number((found.newPrice || "").replace(/[^0-9.]/g, ""));
        return Number.isFinite(parsed) ? parsed : undefined;
      })();

      const pushUpsellEvent = async () => {
        try {
          if (resolvedClientReferenceId) {
            sessionStorage.setItem("clientReferenceId", resolvedClientReferenceId);
          }
        } catch (_) {}
        runAnalytics(() => pushPurchaseEvent({
          clientReferenceId: resolvedClientReferenceId,
          paywall: lastPaywall,
          value: offerValue,
          type: "upsell",
          extraKey: offer.offerId,
        }));
        runAnalytics(() => sendAmplitudePurchase({
          userId: resolvedClientReferenceId,
          revenue: offerValue,
          currency: "usd",
          product: "Upsell combo",
          paywall: lastPaywall,
          type: "upsell",
          extraKey: offer.offerId,
        }));
      };

      const body = {
        offerId: offer.offerId,
        priceId: offer.priceId,
        customerId,
        paymentMethodId,
        data: {
          currency: "usd",
          client_reference_id: resolvedClientReferenceId || clientReferenceId,
          paywall: lastPaywall,
          ...utmMetadata,
          ...acquisitionCountryMetadata,
        },
      };

      const idempotencyKey = `upsell-${offer.offerId}-${customerId}-${Date.now()}`;

      const response = await axios.post(`${API_URL}/upsell-payment`, body, {
        headers: { "Idempotency-Key": idempotencyKey },
      });

      if (!canContinue()) return;
      if (response?.data?.success) {
        sendPaywallEvent("upsell_purchase_completed", {
          route: "/getoffer",
          payload: {
            currency: "usd",
            offer_id: offer.offerId,
            status: "succeeded",
          },
        });
        await pushUpsellEvent();
        goRedirect();
        return;
      }

      if (response?.data?.requires_action && response?.data?.client_secret) {
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          response.data.client_secret
        );
        if (!canContinue()) return;
        if (confirmError) {
          goRedirect();
          return;
        }
        const status = paymentIntent && paymentIntent.status;
        if (
          status === "succeeded" ||
          status === "processing" ||
          status === "requires_capture"
        ) {
          sendPaywallEvent("upsell_purchase_completed", {
            route: "/getoffer",
            payload: {
              currency: "usd",
              offer_id: offer.offerId,
              status,
            },
          });
          await pushUpsellEvent();
          goRedirect();
          return;
        }
        goRedirect();
        return;
      }

      goRedirect();
    } catch (error) {
      if (!canContinue()) return;
      const message = getPaymentLimitMessage(error);
      if (message) setPaymentError(message);
      else goRedirect();
    } finally {
      submitting.current = false;
      if (canContinue()) setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F2F3F5] flex justify-center px-3 py-3 sm:py-6">
      <div className="getoffer w-full max-w-[480px] bg-white rounded-2xl shadow-sm overflow-hidden">
        {/* Header */}
        <div className="px-4 py-2 sm:py-3 border-b border-gray-100">
          <div className="text-base sm:text-lg font-semibold text-[#111827]">ColorAnalysisPro</div>
        </div>

        {/* Stepper */}
        <div className="px-4 pt-2 pb-1.5 sm:pt-4 sm:pb-3">
          <div className="flex items-center justify-between text-xs sm:text-sm text-gray-500">
            <div className="flex flex-col items-center flex-1">
              <div className="h-5 w-5 sm:h-8 sm:w-8 rounded-full border border-gray-300 flex items-center justify-center text-[#7C3A8D] font-semibold text-[10px] sm:text-inherit">
                ✓
              </div>
              <span className="mt-0.5 sm:mt-1 leading-tight">Create account</span>
            </div>
            <div className="flex-1 h-px bg-gray-200 mx-2" />
            <div className="flex flex-col items-center flex-1">
              <div className="h-5 w-5 sm:h-8 sm:w-8 rounded-full border-2 border-[#7C3A8D] text-[#7C3A8D] font-semibold flex items-center justify-center text-[10px] sm:text-inherit">
                2
              </div>
              <span className="mt-0.5 sm:mt-1 text-[#111827] font-semibold leading-tight">Add offer</span>
            </div>
            <div className="flex-1 h-px bg-gray-200 mx-2" />
            <div className="flex flex-col items-center flex-1">
              <div className="h-5 w-5 sm:h-8 sm:w-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-400 font-semibold text-[10px] sm:text-inherit">
                3
              </div>
              <span className="mt-0.5 sm:mt-1 leading-tight">Get the app</span>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="px-5 pt-1.5 pb-3 sm:pt-2 sm:pb-4 text-center">
          <h1 className="text-2xl sm:text-2xl font-bold text-[#111827] leading-tight">
            Exclusive sign-up offer
          </h1>
        </div>

        {/* Offers */}
        <div className="px-4 space-y-2 sm:space-y-3">
          {offers.map((offer) => {
            const selectedState = selected === offer.id;
            return (
              <button
                key={offer.id}
                onClick={() => handleOfferSelect(offer.id)}
                className={`w-full text-left rounded-2xl border transition shadow-[0_4px_10px_rgba(0,0,0,0.04)] ${
                  selectedState ? "border-[#7C3A8D]" : "border-gray-200"
                } bg-white`}
              >
                <div className="flex items-center p-2 sm:p-3 gap-3">
                  <div
                    className="h-12 w-12 sm:h-16 sm:w-16 rounded-xl flex items-center justify-center overflow-hidden"
                    style={{ backgroundColor: offer.iconBg }}
                  >
                    {offer.image ? (
                      <img
                        src={offer.image}
                        alt=""
                        className="h-full w-full object-cover rounded-xl"
                      />
                    ) : (
                      <>
                        <offer.Icon size={24} className="sm:hidden" color={offer.iconColor} strokeWidth={2} />
                        <offer.Icon size={28} className="hidden sm:block" color={offer.iconColor} strokeWidth={2} />
                      </>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm sm:text-base font-semibold text-[#111827] leading-snug line-clamp-2">
                        {offer.title}
                      </p>
                      <IndicatorIcon selected={selectedState} />
                    </div>
                    <div className="flex items-center flex-wrap gap-2 mt-1">
                      <span className="text-sm text-gray-400 line-through">
                        {offer.oldPrice}
                      </span>
                      <span className="text-sm font-semibold text-[#7C3A8D]">
                        {offer.newPrice}
                      </span>
                      <span className="text-[11px] font-semibold text-[#7C3A8D] border border-[#F4A896] bg-[#FFE6D1] rounded-full px-2 py-[2px]">
                        {offer.badge}
                      </span>
                    </div>
                    {offer.description ? (
                      <p className="text-xs text-gray-600 mt-0.5 sm:mt-1 line-clamp-1">
                        {offer.description}
                      </p>
                    ) : null}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Benefits */}
        <div className="px-5 py-3 sm:py-5 space-y-2 sm:space-y-3">
          {benefits.map((benefit, idx) => (
            <div key={idx} className="flex items-start gap-2 sm:gap-3">
              <div className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 rounded-full bg-[#FFE6D1] text-[#7C3A8D] flex items-center justify-center text-[10px] sm:text-xs font-bold mt-[2px]">
                ✓
              </div>
              <p className="text-sm sm:text-base text-[#111827] leading-snug text-left">{benefit}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="px-5 pb-3 sm:pb-4">
          {paymentError && <p role="alert" className="mb-3 text-sm text-red-600 text-center">{paymentError}</p>}
          <button
            onClick={handleGetOffer}
            disabled={loading}
            className={`w-full h-11 sm:h-12 rounded-lg bg-[#7C3A8D] text-white font-semibold text-sm sm:text-base hover:bg-[#A259B5] transition ${
              loading ? "opacity-70 pointer-events-none" : ""
            }`}
          >
            {loading ? "Processing..." : "Get the Offer"}
          </button>
          <button
            onClick={handleSkip}
            className="mt-2 sm:mt-3 w-full text-xs sm:text-sm font-semibold text-[#111827] hover:text-[#7C3A8D] transition"
          >
            Skip the offer and continue
          </button>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-5 py-3 sm:py-4 text-xs sm:text-sm text-gray-500 flex items-center justify-center gap-4">
          <a
            href="https://color-analysis.pro/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-[#7C3A8D] font-medium"
          >
            Privacy
          </a>
          <span className="text-gray-300">|</span>
          <a
            href="https://color-analysis.pro/terms#subscription"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-[#7C3A8D] font-medium"
          >
            Terms
          </a>
        </div>
      </div>
    </div>
  );
};

export default GetOffer;
